import { MAX_PASSENGER_COUNT, ORDER_TYPE_ARRIVAL, ORDER_TYPE_DEPARTURE, OrderType } from '@/data';
import isoCityCodeMap from '@/utils/isoCityCodeMap';
import { format } from 'date-fns';
import Addon from '../Addon';
import Address from '../Address';
import Airport from '../Airport';
import Flight from '../Flight';
import LuggageCounter from '../LuggageCounter';
import Passenger from '../Passenger';
import VehicleType from '../VehicleType';

const DEFAULT_PICK_UP_TIME = (): null => null;
const DEFAULT_RAW_FLIGHT_DATE = (): null => null;

export interface PlacementOrderPayload {
  flight_direction: string;
  airport_id: string;
  pickup_or_dropoff_country_code: string;
  pickup_or_dropoff_city_code: string;
  pickup_or_dropoff_area_code: string;
  pickup_or_dropoff_address: string;
  pickup_at: string;
  vehicle_id: string;
  passenger_count: number;
  luggage_20_inch_count: number;
  luggage_25_inch_count: number;
  luggage_27_inch_count: number;
  luggage_29_inch_count: number;
  has_egate: number;
  flight_at: string;
  flight_number: string;
  special_instructions: string;
  passenger_name: string;
  passenger_email: string;
  passenger_phone_country_code: string;
  passenger_phone_number: string;
  coupon_code_id?: string;
  pickup_or_dropoff_address_input: string;
  addons: Array<{ id: string; count: number }>;
  extra_stop_addresses: string[];
  name_sign_content?: string | null;
  delaycare_count: number;
}

export default class PlacementOrder {
  orderType: OrderType = ORDER_TYPE_ARRIVAL;
  pickUpOrDropOffLocation: Address = new Address();
  airportLocation: Airport = new Airport();
  isPriceTableLoading: boolean = false;
  nameSignContent: string | null = null;

  pickUpTime: Date | null = DEFAULT_PICK_UP_TIME();
  flightInformation: Flight | null = null;

  // 起飛
  rawFlightDate: string | null = DEFAULT_RAW_FLIGHT_DATE();
  rawFlightNumber: string = '';

  passengerCount: number = 1;
  luggageCounter: LuggageCounter = new LuggageCounter();
  vehicleType: VehicleType = new VehicleType();
  description: string = '';

  addons: Addon[] = [];
  extraStops: (Address | null)[] = [];
  isExtraStopsValid: boolean = true;

  passenger: Passenger = new Passenger();
  remoteAreaSurchargeId: string = '';

  /**
   * null 代表不使用
   * string 代表使用
   */
  couponId: string | null = null;

  delayCareCount = 0;

  get defaultPickUpTime(): null {
    return DEFAULT_PICK_UP_TIME();
  }

  get defaultRawFlightDate(): null {
    return DEFAULT_RAW_FLIGHT_DATE();
  }

  // estimate value
  /**
   * @deprecated
   */
  get totalAmount(): number {
    return (
      this.vehicleType.price +
      this.addons.reduce((acc, addon) => acc + addon.price, 0) +
      this.extraStops.length * 200
    );
  }

  get isLocationTimeFlightInformationValid(): boolean {
    return Boolean(
      this.pickUpOrDropOffLocation.city &&
        this.airportLocation.name &&
        this.pickUpTime &&
        this.rawFlightDate &&
        this.rawFlightNumber.length >= 3 &&
        this.flightInformation,
    );
  }

  get isDetailInformationValid(): boolean {
    return Boolean(this.vehicleType.isValid && this.passenger.isValid);
  }

  get flightAt(): string {
    // 當航班資訊失敗時，使用 rawFlightDate 作為航班時間
    if (!this.flightInformation || this.flightInformation.isEmpty) {
      if (this.orderType === ORDER_TYPE_DEPARTURE) {
        // 起飛 依據用戶填寫當地起飛時間
        const pickUpTime = new Date(this.rawFlightDate || '');
        return format(pickUpTime, 'yyyy-MM-dd HH:mm');
      } else if (this.orderType === ORDER_TYPE_ARRIVAL) {
        // 回國 依據用戶填寫當地起飛時間
        // this.rawFlightDate 格式為 YYYY/MM/DD HH:mm
        return (this.rawFlightDate || '').replace(/\//g, '-');
      } else {
        throw new Error('Invalid order type');
      }
    }

    // 依據航班資訊為主
    if (this.orderType === ORDER_TYPE_DEPARTURE) {
      return this.flightInformation.formatDepartureDateTime;
    } else if (this.orderType === ORDER_TYPE_ARRIVAL) {
      // 回國 此時間為落地時間
      return this.flightInformation.formatArrivalDateTime;
    } else {
      throw new Error('Invalid order type');
    }
  }

  constructor(data: Partial<PlacementOrder> = {}) {
    // Set defaults first
    this.orderType = ORDER_TYPE_DEPARTURE;
    this.pickUpOrDropOffLocation = new Address();
    this.airportLocation = new Airport();
    this.pickUpTime = DEFAULT_PICK_UP_TIME();
    this.flightInformation = null;
    this.passengerCount = 1;
    this.luggageCounter = new LuggageCounter();
    this.vehicleType = new VehicleType();
    this.description = '';
    this.addons = [];
    this.extraStops = [];
    this.isExtraStopsValid = true;
    this.passenger = new Passenger();
    this.remoteAreaSurchargeId = '';
    this.rawFlightDate = DEFAULT_RAW_FLIGHT_DATE();
    this.rawFlightNumber = '';
    this.couponId = '';
    this.isPriceTableLoading = false;
    this.nameSignContent = null;

    // Then apply provided data
    Object.assign(this, data);
  }

  clone(): PlacementOrder {
    return new PlacementOrder({
      ...this,
      pickUpOrDropOffLocation: this.pickUpOrDropOffLocation.clone(),
      airportLocation: this.airportLocation.clone(),
      flightInformation: this.flightInformation?.clone() ?? null,
      luggageCounter: this.luggageCounter.clone(),
      vehicleType: this.vehicleType.clone(),
      addons: this.addons.map((addon) => addon.clone()),
      extraStops: this.extraStops.map((address) => address?.clone() ?? address),
      passenger: this.passenger.clone(),
    });
  }

  shallowClone(): PlacementOrder {
    return new PlacementOrder(this);
  }

  setOrderType(orderType: OrderType): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();

    if (![ORDER_TYPE_ARRIVAL, ORDER_TYPE_DEPARTURE].includes(orderType)) {
      return nextPlacementOrder;
    }

    nextPlacementOrder.orderType = orderType;
    return nextPlacementOrder;
  }

  setPickUpOrDropOffLocation(address: Address): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.pickUpOrDropOffLocation = address;
    return nextPlacementOrder;
  }

  setAirportLocation(airport: Airport): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.airportLocation = airport;
    return nextPlacementOrder;
  }

  setPickUpTime(pickUpTime: Date | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.pickUpTime = pickUpTime;
    return nextPlacementOrder;
  }

  setFlightInformation(flightInformation: Flight | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.flightInformation = flightInformation;
    return nextPlacementOrder;
  }

  setPassengerCount(passengerCount: number): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();

    if (passengerCount > MAX_PASSENGER_COUNT) {
      return nextPlacementOrder;
    }

    nextPlacementOrder.passengerCount = passengerCount;
    return nextPlacementOrder;
  }

  setLuggageCounter(luggageCounter: LuggageCounter): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.luggageCounter = luggageCounter;
    return nextPlacementOrder;
  }

  setVehicleType(vehicleType: VehicleType | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();

    if (!vehicleType) {
      nextPlacementOrder.vehicleType = new VehicleType();
      return nextPlacementOrder;
    }

    nextPlacementOrder.vehicleType = vehicleType;
    return nextPlacementOrder;
  }

  setDescription(description: string): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.description = description;
    return nextPlacementOrder;
  }

  setNameSignContent(nameSignContent: string | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.nameSignContent = nameSignContent;
    return nextPlacementOrder;
  }

  setAddon(addon: Addon): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();

    // if addon count === 0 then remove the addon
    if (addon.count === 0) {
      nextPlacementOrder.addons = this.addons.filter((a) => a.id !== addon.id);
      return nextPlacementOrder;
    }

    const existAddonIndex = this.addons.findIndex((a) => a.id === addon.id);
    const nextAddons = [...nextPlacementOrder.addons];
    if (existAddonIndex !== -1) {
      nextAddons[existAddonIndex] = addon;
    } else {
      nextAddons.push(addon);
    }
    nextPlacementOrder.addons = nextAddons;
    return nextPlacementOrder;
  }

  setPassenger(passenger: Passenger): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.passenger = passenger;
    return nextPlacementOrder;
  }

  setExtraStops(extraStops: (Address | null)[]): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.extraStops = extraStops;
    return nextPlacementOrder;
  }

  setIsPriceTableLoading(nextIsPriceTableLoading: boolean): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.isPriceTableLoading = nextIsPriceTableLoading;
    return nextPlacementOrder;
  }

  setIsExtraStopsValid(isValid: boolean): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.isExtraStopsValid = isValid;
    return nextPlacementOrder;
  }

  addExtraStop(address: Address): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    const nextExtraStops = [...nextPlacementOrder.extraStops];
    nextExtraStops.push(address);
    nextPlacementOrder.extraStops = nextExtraStops;
    return nextPlacementOrder;
  }

  removeExtraStop(index: number): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    const nextExtraStops = [...nextPlacementOrder.extraStops];
    nextExtraStops.splice(index, 1);
    nextPlacementOrder.extraStops = nextExtraStops;
    return nextPlacementOrder;
  }

  setRemoteAreaSurchargeId(remoteAreaSurchargeId: string): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.remoteAreaSurchargeId = remoteAreaSurchargeId;
    return nextPlacementOrder;
  }

  setRawFlightDate(rawFlightDate: string | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.rawFlightDate = rawFlightDate;
    return nextPlacementOrder;
  }

  setRawFlightNumber(rawFlightNumber: string): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.rawFlightNumber = rawFlightNumber;
    return nextPlacementOrder;
  }

  setCouponId(couponId: string | null): PlacementOrder {
    const nextPlacementOrder = this.shallowClone();
    nextPlacementOrder.couponId = couponId;
    return nextPlacementOrder;
  }

  getPayload(): PlacementOrderPayload {
    const payload: PlacementOrderPayload = {
      flight_direction: this.orderType,
      airport_id: this.airportLocation.id,
      pickup_or_dropoff_country_code: 'TW',
      pickup_or_dropoff_city_code:
        (isoCityCodeMap as Record<string, string>)[this.pickUpOrDropOffLocation.city] || '',
      pickup_or_dropoff_area_code: this.pickUpOrDropOffLocation.zipCode,
      pickup_or_dropoff_address: this.pickUpOrDropOffLocation.address,
      pickup_at: format(this.pickUpTime || new Date(), 'yyyy-MM-dd HH:mm'),
      vehicle_id: this.vehicleType.id,
      passenger_count: this.passengerCount,
      luggage_20_inch_count: this.luggageCounter.luggage20InchCount,
      luggage_25_inch_count: this.luggageCounter.luggage25InchCount,
      luggage_27_inch_count: this.luggageCounter.luggage27InchCount,
      luggage_29_inch_count: this.luggageCounter.luggage29InchCount,

      has_egate: 0, // 寫死的不知道什麼東西，最遠古的版本就有
      flight_at: this.flightAt,
      flight_number: this.rawFlightNumber,
      special_instructions: this.description,
      passenger_name: this.passenger.name,
      passenger_email: this.passenger.email,
      passenger_phone_country_code: '886', //TODO: fixed val
      passenger_phone_number: this.passenger.phoneNumber,
      pickup_or_dropoff_address_input: this.pickUpOrDropOffLocation.addressInput,
      delaycare_count: this.delayCareCount,

      addons: this.addons.map((addon) => {
        return { id: addon.id, count: addon.count };
      }),

      extra_stop_addresses: this.extraStops
        .filter((extraStop) => extraStop !== null)
        .map((extraStop) => {
          return extraStop.fullAddress;
        }),
    };

    if (this.couponId) {
      payload.coupon_code_id = this.couponId;
    }

    if (this.orderType === ORDER_TYPE_ARRIVAL) {
      payload.name_sign_content = this.nameSignContent;
    }

    return payload;
  }
}
